import { gql, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY } from '../admin/team/AllowedIframeHostsSettings';
import EmojiAutocompleter from '../editor/controls/EmojiAutocompleter';
import MentionPicker from '../editor/controls/MentionPicker';
import SlashCommandMenu from '../editor/controls/SlashCommandMenu';
import EditorToolbar from '../editor/controls/toolbar/EditorToolbar';
import ImageToolbar from '../editor/controls/toolbar/ImageToolbar';
import QuillComponent from '../editor/QuillComponent';
import { useQuill, withQuillContextProvider } from '../editor/QuillContext';
import useLinkShortcut from '../editor/useLinkShortcut';
import type {
  TemplateContent_OwnerFragment,
  TemplateContent_TemplateFragment,
  TemplateContent_TemplatePostFragment,
  TemplateContent_TopicFragment,
} from '../graphql/generated/graphqlTypes';
import PostSkeleton from '../post/PostSkeleton';
import useNavigate from '../utils/router/useNavigate';
import postPath from '../utils/url/postPath';
import styles from './TemplateContent.module.scss';
import TemplateHeader from './TemplateHeader';
import type { TemplateOwnerEntity } from './TemplateOwnerSelect';

const EDITOR_MODULE_CONFIG = {
  headerCollapse: true,
  hoveredBlotListener: true,
  prefixListener: true,
};

const TEMPLATE_CONTENT_TEMPLATE_FRAGMENT = gql`
  fragment TemplateContent_template on Template {
    id
    title
    content
  }
`;

const TEMPLATE_CONTENT_TEMPLATE_POST_FRAGMENT = gql`
  fragment TemplateContent_templatePost on Post {
    id
    title
    content
  }
`;

const TEMPLATE_CONTENT_OWNER_FRAGMENT = gql`
  fragment TemplateContent_owner on User {
    id
    ...TemplateHeader_owner
  }
  ${TemplateHeader.fragments.owner}
`;

const TEMPLATE_CONTENT_TOPIC_FRAGMENT = gql`
  fragment TemplateContent_topic on Topic {
    id
    ...TemplateHeader_topic
  }
  ${TemplateHeader.fragments.topic}
`;

interface TemplateContentProps {
  template:
    | TemplateContent_TemplateFragment
    | TemplateContent_TemplatePostFragment
    | null;
  topics: TemplateContent_TopicFragment[];
  owner: TemplateContent_OwnerFragment | null;
  loading: boolean;
  updateTemplateContent?: (content: string) => Promise<unknown> | null;
  updateTemplateOwner?: (topicOwner?: string) => Promise<unknown> | null;
  deleteTemplate: () => Promise<void>;
  onCreatePost?: () => void;
}

const BaseTemplateContent = ({
  template,
  topics,
  owner,
  loading,
  updateTemplateContent,
  updateTemplateOwner,
  deleteTemplate,
  onCreatePost,
}: TemplateContentProps) => {
  const { data } = useQuery(ORGANIZATION_ALLOWED_EMBED_HOSTS_QUERY);
  const allowedEmbedHosts =
    data?.session?.organization?.settings?.allowedEmbedHosts || [];
  const { quill } = useQuill();
  useLinkShortcut();

  const [isChanged, setIsChanged] = useState(false);

  const [editingState, setEditingState] = useState<
    'none' | 'editing' | 'changingLocation'
  >('none');
  const isEditing = editingState === 'editing';

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEditingState('none');
  }, [template]);

  const templateContent = template && !loading ? template.content : null;

  const navigate = useNavigate();
  const onEditTemplate = () => {
    if (template?.__typename === 'Post') {
      navigate(postPath(template), { state: { edit: true } });
    } else {
      setEditingState('editing');
      if (quill) setTimeout(() => quill.focus(), 10);
    }
  };

  const onChangeLocation = () => {
    if (template?.__typename === 'Post') {
      navigate(postPath(template), { state: { edit: true, editTopic: true } });
    } else {
      setEditingState('changingLocation');
    }
  };

  const onCancel = () => {
    setEditingState('none');
    if (quill && isChanged) {
      setIsChanged(false);
      quill.setContents(templateContent);
    }
  };

  const onSave = async (newOwner: TemplateOwnerEntity | null) => {
    if (!template) {
      return;
    }

    setIsSaving(true);

    if (isChanged && quill && updateTemplateContent) {
      await updateTemplateContent(JSON.stringify(quill.getContents().ops));
    }

    if (newOwner && updateTemplateOwner) {
      await updateTemplateOwner(
        newOwner.entityType === 'topic' ? newOwner.id : undefined,
      );
    }

    setIsSaving(false);
    setEditingState('none');
    setIsChanged(false);
  };

  return (
    <>
      {template && !loading && (
        <TemplateHeader
          templateId={template.id}
          topics={topics}
          owner={owner}
          onEditTemplate={onEditTemplate}
          onChangeLocation={onChangeLocation}
          onCancel={onCancel}
          onSave={onSave}
          onDelete={deleteTemplate}
          onCreatePost={onCreatePost}
          editingState={editingState}
          changed={isChanged}
          saving={isSaving}
        />
      )}
      <div className={styles.editorContainer}>
        {templateContent ? (
          <div className={styles.editor}>
            {quill && isEditing && (
              <>
                <EditorToolbar readOnly={false} />
                <MentionPicker quill={quill} readOnly={false} />
                <EmojiAutocompleter quill={quill} readOnly={false} />
                <ImageToolbar />
              </>
            )}
            <QuillComponent
              namespace="template"
              content={templateContent}
              titlePlaceholder="Untitled"
              readOnly={!isEditing}
              moduleConfig={{
                ...EDITOR_MODULE_CONFIG,
                clipboard: { allowedEmbedHosts },
              }}
              rootClassName={styles.editorRoot}
              onTextChange={() => {
                if (quill && editingState === 'editing') {
                  setIsChanged(true);
                }
              }}
            />
            {quill && isEditing && (
              <SlashCommandMenu
                quill={quill}
                readOnly={false}
                allowedEmbedHosts={allowedEmbedHosts}
              />
            )}
          </div>
        ) : (
          <div className={styles.loader}>
            <PostSkeleton />
          </div>
        )}
      </div>
    </>
  );
};

const TemplateContent = withQuillContextProvider(BaseTemplateContent);

// @ts-expect-error
TemplateContent.fragments = {
  template: TEMPLATE_CONTENT_TEMPLATE_FRAGMENT,
  owner: TEMPLATE_CONTENT_OWNER_FRAGMENT,
  topic: TEMPLATE_CONTENT_TOPIC_FRAGMENT,
  templatePost: TEMPLATE_CONTENT_TEMPLATE_POST_FRAGMENT,
};

export default TemplateContent;
